import * as React from "react";
import { PACKAGE_NUMBERS } from "@ewn/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { countFrom } from "@/lib/format";

const [MIN, MAX] = PACKAGE_NUMBERS.discount_pct;

/**
 * One discount for the packages ticked in the table: "10% off these".
 *
 * Shown only while something is ticked, so the table stays the page. The
 * percent is checked against `PACKAGE_NUMBERS`, the table the worker checks
 * it against too, and the worker decides.
 */
export function BulkDiscount({
  count,
  busy,
  onApply,
  onClear,
}: {
  count: number;
  busy: boolean;
  onApply: (pct: number) => void;
  onClear: () => void;
}) {
  const [text, setText] = React.useState("10");
  const [wrong, setWrong] = React.useState<string | null>(null);

  function apply() {
    const pct = countFrom(text);
    if (pct === null || pct < MIN || pct > MAX) {
      setWrong(`A whole percent from ${MIN} to ${MAX}`);
      return;
    }
    setWrong(null);
    onApply(pct);
  }

  return (
    <div
      role="region"
      aria-label="Discount for the chosen packages"
      className={
        "flex flex-wrap items-center gap-3 rounded-lg border border-border " +
        "bg-muted/40 px-4 py-3"
      }
    >
      <span className="text-sm font-medium">
        {count} {count === 1 ? "package" : "packages"} chosen
      </span>
      <label htmlFor="bulk-discount" className="text-sm text-secondary">
        Discount (%)
      </label>
      <Input
        id="bulk-discount"
        inputMode="numeric"
        className="w-20"
        aria-invalid={wrong ? true : undefined}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button size="sm" disabled={busy} onClick={apply}>
        Apply to {count === 1 ? "it" : `all ${count}`}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
        Clear
      </Button>
      {wrong && <span className="w-full text-xs text-destructive-ink">{wrong}</span>}
      <span className="w-full text-xs text-muted-foreground">
        0 takes a discount off. It adds to the crypto discount, and checkout charges it at once.
      </span>
    </div>
  );
}
